// The trowel's block wheel — pure geometry, shared by the renderer (which draws it) and
// input (which hit-tests the pointer against it), so the two can't disagree about where a
// wedge is. No DOM, no canvas: just "what does the player own" and "which wedge is the
// pointer over".

import { SOLID_MATERIALS } from "../../../engine/tiles.js";
import { paintColor } from "../../../engine/palette.js";

// A representative colour for a custom block's design (its first painted pixel), for the wheel wedge +
// swatch. All-blank → the neutral custom fill.
function repColor(def) {
    if (def) for (const v of def) if (v >= 0) return paintColor(v);
    return "#3a3550";
}

// The block types the player actually has (count > 0), each with the colours the wheel and inventory
// draw it in. First the fixed materials (in material order), then any CUSTOM blocks (block-c<id>, Set
// 19.2) sorted by id — so the same order comes out of every call and a hover index maps to one block.
// `inv` is the player's INVENTORY map (item -> count); `defOf(id)` (optional) supplies a custom block's
// 6×6 design for drawing (the hit-test doesn't need it, so InputService omits it).
export function ownedBlocks(inv, defOf) {
    if (!inv) return [];
    const mats = SOLID_MATERIALS
        .filter((m) => (inv[m.name] || 0) > 0)
        .map((m) => ({ name: m.name, fill: m.fill, line: m.line, count: inv[m.name] }));
    const customs = Object.keys(inv)
        .filter((k) => (inv[k] || 0) > 0 && /^block-c\d+$/.test(k))
        .sort((a, b) => (+a.slice(7)) - (+b.slice(7)))
        .map((k) => {
            const id = +k.slice(7), def = defOf ? defOf(id) : null;
            return { name: k, fill: repColor(def), line: "#6a6488", count: inv[k], custom: true, def, label: "block " + id };
        });
    return [...mats, ...customs];
}

// Which wedge (0..n-1) the pointer offset (dx, dy) from the wheel centre selects, or -1 if
// it's inside the dead zone (too close to the middle to commit) or there are no wedges.
// Wedge 0 is centred at the top and they run clockwise — the same order the renderer lays
// them out.
export function hoverIndex(dx, dy, n, deadzone = 24) {
    if (n <= 0) return -1;
    if (dx * dx + dy * dy < deadzone * deadzone) return -1;
    let a = Math.atan2(dy, dx) + Math.PI / 2;              // 0 = straight up
    a = ((a % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    return Math.round(a / ((2 * Math.PI) / n)) % n;
}
